import { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, DollarSign } from 'lucide-react';
import type { ClientPayment } from '@peles/shared';
import { formatDateOnly } from '@peles/shared';
import { useEventClientLedger, useDeleteClientPayment } from '../../hooks/useClientPayments';
import { useDialog } from '../../context/DialogContext';
import { ClientPaymentModal } from './ClientPaymentModal';

interface Props {
  eventId: string;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

export function ClientLedgerView({ eventId }: Props) {
  const { data: ledger, isLoading } = useEventClientLedger(eventId);
  const deletePayment = useDeleteClientPayment();
  const { confirm } = useDialog();
  const [showAdd, setShowAdd] = useState(false);
  const [editPayment, setEditPayment] = useState<ClientPayment | null>(null);
  const [expandedPayment, setExpandedPayment] = useState<string | null>(null);

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading client ledger…</p>;
  }
  if (!ledger) return null;

  const balanceColor =
    ledger.balanceDue > 0.005
      ? 'text-red-600'
      : ledger.balanceDue < -0.005
        ? 'text-amber-600'
        : 'text-green-700';

  const payableExpenses = ledger.expenses.filter((e) => e.isProducerFunded);

  async function handleDelete(payment: ClientPayment) {
    if (
      !(await confirm({
        title: 'Delete Payment',
        message: 'Delete this payment? Allocations will also be removed.',
        confirmLabel: 'Delete',
      }))
    )
      return;
    await deletePayment.mutateAsync({ id: payment.id, eventId: payment.eventId });
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <DollarSign size={18} className="text-emerald-600" />
          <h2 className="text-lg font-semibold text-gray-900">Client Ledger</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
        >
          <Plus size={16} /> Record Payment
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card label="Total Owed" value={fmt(ledger.totalOwed)} />
        <Card label="Total Received" value={fmt(ledger.totalReceived)} />
        <Card label="Balance Due" value={fmt(ledger.balanceDue)} valueClassName={balanceColor} />
        <Card label="Unallocated Credit" value={fmt(Math.max(0, ledger.unallocated))} />
      </div>

      {/* Income vs Reimbursement breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
        <div className="rounded-xl border border-gray-200 px-4 py-3">
          <div className="text-xs text-gray-500 uppercase">Our Services (Income owed)</div>
          <div className="text-lg font-semibold mt-1">{fmt(ledger.incomeOwed)}</div>
        </div>
        <div className="rounded-xl border border-gray-200 px-4 py-3">
          <div className="text-xs text-gray-500 uppercase">Vendor Reimbursements owed</div>
          <div className="text-lg font-semibold mt-1">{fmt(ledger.reimbursementOwed)}</div>
        </div>
      </div>

      {/* Per-vendor table */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">By Vendor</h3>
        {ledger.vendorRows.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No producer-funded expenses on this event.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Items</th>
                  <th className="px-3 py-2 text-right">Owed</th>
                  <th className="px-3 py-2 text-right">Paid by Client</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.vendorRows.map((row) => (
                  <tr key={row.vendorId ?? '__unassigned__'} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      {row.vendorName}
                      {row.isProducer && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-700">
                          us
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 capitalize text-gray-600">{row.chargeType}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.expenseItemCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(row.totalOwed)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {fmt(row.totalPaid)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums font-medium ${
                        row.balance > 0.005 ? 'text-red-600' : 'text-green-700'
                      }`}
                    >
                      {fmt(row.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Payments list */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Payments Received</h3>
        {ledger.payments.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No payments recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {ledger.payments.map((p) => {
              const isExpanded = expandedPayment === p.id;
              return (
                <div key={p.id} className="border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setExpandedPayment(isExpanded ? null : p.id)}
                      className="text-gray-500 hover:text-gray-700"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
                      <div>
                        <div className="text-xs text-gray-500">Date</div>
                        <div>{formatDateOnly(p.paidAt)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Method</div>
                        <div>{p.paymentMethodName}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Reference</div>
                        <div className="truncate">{p.reference ?? '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Amount</div>
                        <div className="font-semibold">{fmt(p.amount)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Unallocated</div>
                        <div className={p.unallocated > 0.005 ? 'text-amber-600' : 'text-gray-600'}>
                          {fmt(p.unallocated)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditPayment(p)}
                        className="p-1.5 text-gray-500 hover:text-primary-600 rounded"
                        aria-label="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p)}
                        className="p-1.5 text-gray-500 hover:text-red-600 rounded"
                        aria-label="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 px-3 py-2 bg-gray-50">
                      {p.allocations.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No allocations.</p>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="text-gray-500">
                            <tr>
                              <th className="px-2 py-1 text-left">Vendor</th>
                              <th className="px-2 py-1 text-left">Item</th>
                              <th className="px-2 py-1 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.allocations.map((a) => (
                              <tr key={a.id}>
                                <td className="px-2 py-1">{a.expenseVendorName ?? '—'}</td>
                                <td className="px-2 py-1">{a.expenseItemName ?? '—'}</td>
                                <td className="px-2 py-1 text-right tabular-nums">
                                  {fmt(a.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {p.notes && <p className="mt-2 text-xs text-gray-600 italic">{p.notes}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Modals */}
      <ClientPaymentModal
        open={showAdd}
        eventId={eventId}
        payableExpenses={payableExpenses}
        onClose={() => setShowAdd(false)}
      />
      {editPayment && (
        <ClientPaymentModal
          open={true}
          eventId={eventId}
          payableExpenses={payableExpenses}
          payment={editPayment}
          onClose={() => setEditPayment(null)}
        />
      )}
    </div>
  );
}

function Card({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3">
      <div className="text-xs text-gray-500 uppercase">{label}</div>
      <div className={`text-xl font-bold mt-1 ${valueClassName ?? 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
